<template>
  <div class="home">
    <!-- ============== HERO ============== -->
    <section class="hero">
      <div class="lb-container hero__grid">
        <div class="hero__copy">
          <SectionLabel text="Klare Zahn-Tarif-Empfehlung — telefonisch" />

          <h1 class="hero__headline">
            Sie wollen die richtige
            <span class="hero__highlight">Zahnzusatz&shy;versicherung</span>
            abschließen?
          </h1>

          <p class="hero__sub">
            Ich helfe Ihnen dabei. <strong>Ohne selbst zu vergleichen.</strong>
            <strong>Ohne Stress.</strong>
            <br />
            Persönliche Telefonberatung für
            <span class="hero__accent">60Plus Patienten</span>
            mit klarer Tarif-Empfehlung.
          </p>

          <div class="hero__cta">
            <CtaButton to="/sprechstunde" size="xl">
              Termin jetzt sichern — kostenlos
            </CtaButton>
            <div class="hero__cta-meta">
              <span class="lb-handwritten">dauert nur 10–15 Min.</span>
              <HandArrow :size="60" color="#0B1F3B" :rotate="-15" />
            </div>
          </div>

          <ul class="hero__trust">
            <li><strong>100 % kostenlos</strong></li>
            <li>Keine Vorbereitung nötig</li>
            <li>Sie entscheiden in Ruhe</li>
          </ul>
        </div>

        <div class="hero__visual">
          <div class="hero__portrait">
            <img
              :src="kili2Img"
              alt="Kilian Link — Ihr Ansprechpartner für die Zahnzusatzversicherung-Beratung"
              class="hero__portrait-img"
            />
            <div class="hero__sticker">
              <SparkleIcon :size="20" color="#F59E0B" />
              <span>Kilian Link · Ihr Berater</span>
            </div>
          </div>
          <div class="hero__card">
            <h4>Termin zur Sprechstunde</h4>
            <ul>
              <li>Dauer: ca. 10–15 Minuten</li>
              <li>Sie entscheiden in Ruhe</li>
              <li>100 % kostenlos</li>
            </ul>
            <CtaButton to="/sprechstunde" size="md" block>
              Termin sichern
            </CtaButton>
          </div>
        </div>
      </div>
    </section>

    <!-- ============== BLOCK 2 — Probleme ============== -->
    <section class="lb-section problems">
      <div class="lb-container">
        <div class="problems__head">
          <SectionLabel text="Vielleicht kennen Sie das" />
          <h2>Kommt Ihnen das bekannt vor?</h2>
        </div>

        <div class="problems__grid">
          <div class="problem-card">
            <div class="problem-card__cross">✕</div>
            <h3>Zu viele Zahn-Tarife</h3>
            <p>
              Hunderte Angebote, unzählige Bausteine — und nach drei Vergleichsrechnern
              sind Sie verwirrter als vorher.
            </p>
          </div>

          <div class="problem-card">
            <div class="problem-card__cross">✕</div>
            <h3>Keine klare Entscheidung</h3>
            <p>
              Sie wollen handeln, aber irgendetwas fehlt — eine ehrliche Empfehlung,
              die Ihre Situation wirklich versteht.
            </p>
          </div>

          <div class="problem-card">
            <div class="problem-card__cross">✕</div>
            <h3>Angst, das Falsche abzuschließen</h3>
            <p>
              Versicherungs-Kleingedrucktes, Wartezeiten, Begrenzungen — Sie wollen
              nichts falsch machen.
            </p>
          </div>
        </div>

        <div class="problems__bridge">
          <HandArrow :size="60" color="#6C4CF1" />
          <p class="problems__hook">
            Genau hier setzt die
            <span class="problems__brand">LinkMethode™</span> an.
          </p>
        </div>
      </div>
    </section>

    <!-- ============== BLOCK 3 — Linkmethode ============== -->
    <section class="lb-section lb-section--cream method">
      <div class="lb-container">
        <div class="method__head">
          <SectionLabel text="Mein Beratungsansatz" />
          <h2>Die LinkMethode™</h2>
          <p class="method__lead">
            Ein System, das dafür entwickelt wurde,
            <strong>schnell und sicher</strong> den für Sie passenden Zahn-Tarif zu finden.
          </p>
        </div>

        <div class="method__steps">
          <div class="method-step">
            <div class="method-step__num">1</div>
            <h3>Analyse Ihrer Situation</h3>
            <p>Wir besprechen Ihre Zähne, Ihr Alter, Ihr Budget und Ihre Ziele.</p>
          </div>
          <div class="method-step">
            <div class="method-step__num">2</div>
            <h3>Bewertung der besten Tarife</h3>
            <p>Auf Basis der WaizmannTabelle wähle ich die Tarife aus, die zu Ihnen passen.</p>
          </div>
          <div class="method-step">
            <div class="method-step__num">3</div>
            <h3><strong>Klare</strong> Empfehlung</h3>
            <p>Sie bekommen <strong>genau einen Tarif empfohlen</strong> — verständlich begründet.</p>
          </div>
        </div>

        <div class="method__benefits">
          <h3>Das bedeutet für Sie:</h3>
          <ul>
            <li><span class="check">✔</span> Keine Tarifflut</li>
            <li><span class="check">✔</span> Keine Unsicherheit</li>
            <li><span class="check">✔</span> Keine Fehlentscheidung</li>
          </ul>
          <p class="method__bridge">
            <span class="lb-handwritten">Sondern:</span>
            <strong>Eine klare Entscheidung</strong>, die wirklich passt.
          </p>
        </div>

        <div class="method__highlight">
          <SparkleIcon :size="28" color="#F59E0B" />
          <h2>
            Sie bekommen eine <em>Entscheidung</em> — keine Auswahl.
          </h2>
          <SparkleIcon :size="28" color="#F59E0B" />
        </div>
      </div>
    </section>

    <!-- ============== BLOCK 4 — Ablauf ============== -->
    <section class="lb-section flow">
      <div class="lb-container">
        <div class="flow__head">
          <SectionLabel text="Ihre Sprechstunde" />
          <h2>So einfach geht’s</h2>
        </div>

        <ol class="flow__timeline">
          <li class="flow-step">
            <span class="flow-step__bullet">📅</span>
            <h3>Termin buchen</h3>
            <p>In wenigen Klicks online den Wunschtermin auswählen.</p>
          </li>
          <li class="flow-step">
            <span class="flow-step__bullet">📞</span>
            <h3>Telefonberatung erhalten</h3>
            <p>Wir telefonieren in Ruhe — 10 bis 15 Minuten, ganz unverbindlich.</p>
          </li>
          <li class="flow-step">
            <span class="flow-step__bullet">🎯</span>
            <h3>Empfehlung erhalten</h3>
            <p>Sie erhalten <strong>eine klare Empfehlung</strong> — genau einen Tarif, der zu Ihrer Situation passt.</p>
          </li>
          <li class="flow-step">
            <span class="flow-step__bullet">✉️</span>
            <h3>Unterlagen erhalten</h3>
            <p><strong>Unterschriftsreife Unterlagen per Post oder E-Mail</strong>. Sie entscheiden in Ruhe.</p>
          </li>
        </ol>

        <ul class="flow__trust">
          <li><span class="check">✔</span> Keine Vorbereitung nötig</li>
          <li><span class="check">✔</span> Verständlich erklärt</li>
          <li><span class="check">✔</span> Kostenlos</li>
        </ul>
      </div>
    </section>

    <!-- ============== BLOCK 5 — Zielgruppe ============== -->
    <section class="lb-section lb-section--cream target">
      <div class="lb-container lb-container--narrow target__inner">
        <SectionLabel text="Genau für Sie?" />
        <h2>Für wen ist das ideal?</h2>
        <ul class="target__list">
          <li><SparkleIcon :size="20" color="#F59E0B" /> Sie möchten <strong>nicht selbst</strong> vergleichen.</li>
          <li><SparkleIcon :size="20" color="#F59E0B" /> Sie wollen <strong>eine klare Empfehlung</strong>.</li>
          <li><SparkleIcon :size="20" color="#F59E0B" /> Sie bevorzugen eine <strong>persönliche Beratung</strong>.</li>
        </ul>

        <div class="target__highlight">
          <span class="target__chip">Besonders geeignet für:</span>
          <h3>Menschen 60+</h3>
        </div>
      </div>
    </section>

    <!-- ============== BLOCK 6 — Über ============== -->
    <section class="lb-section about">
      <div class="lb-container about__grid">
        <div class="about__image">
          <div class="about__portrait">
            <img
              :src="kili1Img"
              alt="Kilian Link — Versicherungsvermittler aus München-Untergiesing"
              class="about__portrait-img"
            />
          </div>
          <div class="about__caption">
            <SparkleIcon :size="18" color="#F59E0B" />
            <span>Kilian Link · 47 · München-Untergiesing</span>
          </div>
        </div>

        <div class="about__copy">
          <SectionLabel text="Ihr Ansprechpartner" />
          <h2>Hallo, ich bin Kilian.</h2>

          <p>
            Seit über <strong>15 Jahren</strong> beschäftige ich mich
            ausschließlich mit Zahnzusatzversicherungen.
            Ich habe gesehen, wie schwierig es für viele Menschen ist,
            die richtige Entscheidung zu treffen — also den individuell
            passenden Zahn-Tarif zu finden.
          </p>

          <p>
            Ich berate Menschen, die <strong>keine Lust auf komplizierte Vergleichsrechner</strong>
            haben, sondern eine verständliche und persönliche Empfehlung möchten.
            Versicherungen müssen nicht kompliziert erklärt werden.
          </p>

          <p class="about__do">
            Deshalb mache ich es einfach:
            <br />
            Ich analysiere Ihre Situation und empfehle Ihnen genau den Tarif, der passt.
            <br />
            <span class="lb-handwritten">Ohne Umwege. Ohne Verwirrung.</span>
          </p>

          <ul class="about__credentials">
            <li><span class="check">✔</span> Spezialist auf Zahnzusatzversicherungen</li>
            <li><span class="check">✔</span> Über 15 Jahre Erfahrung in der Tarif-Analyse</li>
            <li><span class="check">✔</span> Geprüfter Versicherungsvermittler IHK (§ 34d GewO)</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ============== BLOCK 7 — Vertrauen / Waizmann ============== -->
    <section class="lb-section lb-section--cream trust">
      <div class="lb-container lb-container--narrow trust__inner">
        <SectionLabel text="Fundierte Grundlage" />
        <h2>Die Nr. 1 bei Zahnärzten als Basis</h2>
        <p class="trust__lead">
          Für meine Empfehlungen nutze ich die bewährten Analysen der
          <strong>WaizmannTabelle</strong>.
        </p>

        <div class="trust__card">
          <div class="trust__big">11.000+</div>
          <p>
            <strong>Über 11.000 Zahnärzte</strong> in Deutschland empfehlen
            bereits die WaizmannTabelle.
          </p>
          <p class="trust__since">
            <SparkleIcon :size="18" color="#F59E0B" />
            Das Original — seit 1995.
          </p>
        </div>

        <p class="trust__goal">
          <span class="lb-handwritten">Mein Ziel:</span>
          Ihnen eine klare Entscheidung zu ermöglichen.
        </p>
      </div>
    </section>

    <!-- ============== BLOCK 8 — Final CTA ============== -->
    <section class="lb-section lb-section--dark final-cta">
      <div class="lb-container lb-container--narrow final-cta__inner">
        <SectionLabel text="Nächster Schritt" />
        <h2>Jetzt Klarheit schaffen</h2>
        <p>In wenigen Minuten zur richtigen Entscheidung.</p>

        <CtaButton to="/sprechstunde" size="xl">
          Termin jetzt buchen
        </CtaButton>

        <p class="final-cta__meta">
          Dauer ca. 10–15 Minuten · 100 % kostenlos · keine Vorbereitung nötig
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, inject } from "vue";
import SectionLabel from "@/components/ui/SectionLabel.vue";
import CtaButton from "@/components/ui/CtaButton.vue";
import HandArrow from "@/components/ui/HandArrow.vue";
import SparkleIcon from "@/components/ui/SparkleIcon.vue";
import kili1Img from "@/assets/Kili1.png";
import kili2Img from "@/assets/Kili2.png";

const updateDocumentHead = inject("updateDocumentHead", null);

onMounted(() => {
  if (updateDocumentHead) {
    updateDocumentHead(
      "Zahnzusatzversicherung 60 Plus Beratung telefonisch | Kilian Link",
      "Persönliche Telefonberatung für Zahnzusatzversicherungen. Klare Empfehlung statt Tarifvergleich. Jetzt Termin sichern."
    );
  }
});
</script>

<style scoped>
/* ============== HERO ============== */
.hero {
  background:
    radial-gradient(1200px 600px at 80% -10%, rgba(108, 76, 241, 0.08), transparent 60%),
    radial-gradient(900px 500px at -10% 30%, rgba(245, 158, 11, 0.08), transparent 60%),
    #fff;
  padding: 80px 0 120px;
}

.hero__grid {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 64px;
  align-items: center;
}

.hero__headline {
  margin: 22px 0 24px;
  color: var(--lb-ink);
}

.hero__highlight {
  background: linear-gradient(180deg, transparent 60%, rgba(245, 158, 11, 0.35) 60%);
  padding: 0 4px;
}

.hero__sub {
  font-size: clamp(17px, 1.4vw, 21px);
  color: var(--lb-ink-soft);
  margin-bottom: 36px;
}

.hero__accent {
  color: var(--lb-accent);
  font-weight: 700;
}

.hero__cta {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}

.hero__cta-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 18px;
}

.hero__trust {
  list-style: none;
  padding: 0;
  margin: 24px 0 0;
  display: flex;
  gap: 22px;
  flex-wrap: wrap;
  color: var(--lb-ink-soft);
  font-size: 15px;
}

.hero__trust li::before {
  content: "✔";
  color: var(--lb-cta);
  font-weight: 900;
  margin-right: 6px;
}

.hero__visual {
  position: relative;
}

.hero__portrait {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  background: var(--lb-cream);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(11, 31, 59, 0.12);
}

.hero__portrait-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Caveat", cursive;
  font-size: 28px;
  color: var(--lb-mute);
  text-align: center;
  border: 2px dashed var(--lb-line);
  border-radius: 24px;
}

.hero__portrait-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  border-radius: 24px;
  display: block;
}

.hero__sticker {
  position: absolute;
  bottom: -18px;
  left: -18px;
  background: #fff;
  padding: 10px 16px;
  border-radius: 999px;
  box-shadow: 0 16px 30px rgba(11, 31, 59, 0.12);
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Caveat", cursive;
  font-size: 20px;
  color: var(--lb-ink);
}

.hero__card {
  margin-top: 36px;
  background: var(--lb-ink);
  color: #fff;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 24px 50px rgba(11, 31, 59, 0.2);
}

.hero__card h4 {
  color: #fff;
  font-size: 22px;
  margin-bottom: 14px;
}

.hero__card ul {
  list-style: none;
  padding: 0;
  margin: 0 0 18px;
}

.hero__card li {
  color: #cbd5e1;
  padding: 6px 0;
  font-size: 15px;
}

.hero__card li::before {
  content: "✔";
  color: var(--lb-cta);
  margin-right: 8px;
}

@media (max-width: 960px) {
  .hero {
    padding: 60px 0 80px;
  }
  .hero__grid {
    grid-template-columns: 1fr;
    gap: 48px;
  }
  .hero__visual {
    order: -1;
  }
  .hero__portrait {
    max-width: 420px;
    margin: 0 auto;
  }
}

/* ============== PROBLEMS ============== */
.problems__head {
  text-align: center;
  margin-bottom: 56px;
}

.problems__head h2 {
  margin-top: 12px;
}

.problems__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.problem-card {
  background: var(--lb-cream);
  border: 1px solid var(--lb-line);
  padding: 36px 28px;
  border-radius: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.problem-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(11, 31, 59, 0.08);
}

.problem-card__cross {
  width: 44px;
  height: 44px;
  background: #fff;
  border: 2px solid var(--lb-cta);
  color: var(--lb-cta);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 20px;
  margin-bottom: 18px;
}

.problem-card h3 {
  margin-bottom: 10px;
}

.problem-card p {
  margin: 0;
  font-size: 16px;
}

.problems__bridge {
  margin-top: 64px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.problems__hook {
  font-family: "Playfair Display", serif;
  font-size: clamp(22px, 2.4vw, 30px);
  font-weight: 600;
  color: var(--lb-ink);
  max-width: 640px;
  margin: 0 auto;
}

.problems__brand {
  color: var(--lb-accent);
  font-style: italic;
  font-weight: 700;
}

@media (max-width: 860px) {
  .problems__grid {
    grid-template-columns: 1fr;
  }
}

/* ============== LINKMETHODE ============== */
.method__head {
  text-align: center;
  margin-bottom: 56px;
}

.method__head h2 {
  margin-top: 12px;
}

.method__lead {
  max-width: 700px;
  margin: 14px auto 0;
  font-size: clamp(17px, 1.4vw, 20px);
}

.method__steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  margin-bottom: 64px;
}

.method-step {
  text-align: center;
  background: #fff;
  border-radius: 20px;
  padding: 36px 28px;
  border: 1px solid var(--lb-line);
}

.method-step__num {
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: var(--lb-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Playfair Display", serif;
  font-size: 28px;
  font-weight: 800;
  box-shadow: 0 12px 24px rgba(108, 76, 241, 0.25);
}

.method-step p {
  margin: 0;
}

.method__benefits {
  text-align: center;
  margin: 0 auto 56px;
  max-width: 640px;
}

.method__benefits h3 {
  font-size: 22px;
  margin-bottom: 14px;
}

.method__benefits ul {
  list-style: none;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin: 0 0 22px;
}

.method__benefits li {
  font-size: 17px;
  color: var(--lb-ink);
  font-weight: 600;
}

.check {
  color: var(--lb-cta);
  font-weight: 900;
  margin-right: 6px;
}

.method__bridge {
  font-family: "Playfair Display", serif;
  font-size: 22px;
  color: var(--lb-ink);
}

.method__highlight {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  text-align: center;
  background: #fff;
  border: 2px solid var(--lb-cta);
  border-radius: 22px;
  padding: 36px 28px;
  max-width: 820px;
  margin: 0 auto;
}

.method__highlight h2 {
  margin: 0;
  font-size: clamp(22px, 2.6vw, 32px);
}

.method__highlight em {
  color: var(--lb-cta);
  font-style: italic;
}

@media (max-width: 860px) {
  .method__steps {
    grid-template-columns: 1fr;
  }
}

/* ============== FLOW ============== */
.flow__head {
  text-align: center;
  margin-bottom: 56px;
}

.flow__head h2 {
  margin-top: 12px;
}

.flow__timeline {
  list-style: none;
  padding: 0;
  margin: 0 0 48px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  position: relative;
}

.flow__timeline::before {
  content: "";
  position: absolute;
  top: 36px;
  left: 8%;
  right: 8%;
  height: 2px;
  background: repeating-linear-gradient(
    to right,
    var(--lb-cta) 0 8px,
    transparent 8px 16px
  );
  z-index: 0;
}

.flow-step {
  position: relative;
  background: #fff;
  border: 1px solid var(--lb-line);
  border-radius: 20px;
  padding: 28px 22px;
  text-align: center;
  z-index: 1;
}

.flow-step__bullet {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid var(--lb-cta);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin: -52px auto 16px;
  box-shadow: 0 12px 24px rgba(245, 158, 11, 0.18);
}

.flow-step h3 {
  font-size: 20px;
  margin-bottom: 8px;
}

.flow-step p {
  font-size: 15px;
  margin: 0;
}

.flow__trust {
  list-style: none;
  padding: 0;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 28px;
  font-size: 16px;
  color: var(--lb-ink);
}

@media (max-width: 860px) {
  .flow__timeline {
    grid-template-columns: 1fr;
    gap: 60px;
  }
  .flow__timeline::before {
    display: none;
  }
}

/* ============== TARGET ============== */
.target__inner {
  text-align: center;
}

.target__inner h2 {
  margin: 12px 0 28px;
}

.target__list {
  list-style: none;
  padding: 0;
  margin: 0 auto 40px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: 19px;
  max-width: 540px;
  text-align: left;
}

.target__list li {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--lb-ink);
}

.target__highlight {
  background: #fff;
  border: 2px solid var(--lb-accent);
  border-radius: 22px;
  padding: 32px;
  display: inline-block;
}

.target__chip {
  display: inline-block;
  background: var(--lb-accent);
  color: #fff;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.target__highlight h3 {
  margin: 0;
  font-size: clamp(28px, 4vw, 44px);
  color: var(--lb-accent);
}

/* ============== ABOUT ============== */
.about__grid {
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 64px;
  align-items: start;
}

.about__image {
  position: sticky;
  top: 110px;
}

.about__portrait {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  background: var(--lb-cream);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(11, 31, 59, 0.1);
}

.about__portrait-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.about__caption {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Caveat", cursive;
  font-size: 22px;
  color: var(--lb-ink);
}

.about__do {
  background: var(--lb-cream);
  border-left: 4px solid var(--lb-cta);
  padding: 18px 22px;
  border-radius: 8px;
  font-size: 18px;
  color: var(--lb-ink);
}

.about__credentials {
  list-style: none;
  padding: 0;
  margin: 24px 0 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 16px;
}

.about__more {
  display: inline-block;
  color: var(--lb-accent);
  font-weight: 700;
  font-size: 16px;
  text-decoration: underline;
  text-decoration-color: rgba(108, 76, 241, 0.4);
  text-underline-offset: 4px;
}

@media (max-width: 960px) {
  .about__grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .about__image {
    position: static;
    max-width: 420px;
    margin: 0 auto;
  }
}

/* ============== TRUST ============== */
.trust__inner {
  text-align: center;
}

.trust__inner h2 {
  margin: 12px 0 16px;
}

.trust__lead {
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto 36px;
}

.trust__card {
  background: #fff;
  border: 1px solid var(--lb-line);
  border-radius: 24px;
  padding: 40px 32px;
  max-width: 540px;
  margin: 0 auto 32px;
  box-shadow: 0 24px 50px rgba(11, 31, 59, 0.06);
}

.trust__big {
  font-family: "Playfair Display", serif;
  font-size: clamp(56px, 8vw, 96px);
  font-weight: 800;
  color: var(--lb-accent);
  line-height: 1;
  margin-bottom: 14px;
}

.trust__since {
  color: var(--lb-ink);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "Caveat", cursive;
  font-size: 24px;
  margin: 0;
}

.trust__goal {
  font-family: "Playfair Display", serif;
  font-size: clamp(22px, 2.4vw, 28px);
  color: var(--lb-ink);
  max-width: 640px;
  margin: 0 auto;
}

/* ============== FINAL CTA ============== */
.final-cta__inner {
  text-align: center;
}

.final-cta__inner h2 {
  font-size: clamp(36px, 5vw, 60px);
  margin: 12px 0 16px;
}

.final-cta__inner p {
  font-size: 19px;
  margin-bottom: 36px;
}

.final-cta__meta {
  margin-top: 22px;
  color: #94a3b8;
  font-size: 15px;
}
</style>
